import { useEffect, useState } from 'react';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { LoadingSpinner } from '@/components/shared/LoadingSpinner';
import { Search, Gamepad2, Activity } from 'lucide-react';
import { gameService, GameSessionDto, PagedResult } from '@/features/admin/services/gameService';
import { toast } from 'sonner';

export const GameListPage = () => {
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [activeTab, setActiveTab] = useState<'active' | 'history'>('active');
  const [games, setGames] = useState<PagedResult<GameSessionDto> | null>(null);

  useEffect(() => {
    fetchGames();
  }, [activeTab]);

  const fetchGames = async () => {
    try {
      setLoading(true);
      const data = activeTab === 'active'
        ? await gameService.getActiveGames({ pageNumber: 1, pageSize: 50 })
        : await gameService.getGameHistory({ pageNumber: 1, pageSize: 50 });
      setGames(data);
    } catch (error: any) {
      toast.error(error.response?.data?.message || 'فشل تحميل الألعاب');
      console.error('Failed to fetch games:', error);
    } finally {
      setLoading(false);
    }
  };

  const filteredGames = games?.items.filter((g) =>
    g.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    g.roomCode.toLowerCase().includes(searchTerm.toLowerCase())
  ) || [];

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <LoadingSpinner />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4">
        <div>
          <h1 className="text-3xl font-bold text-accent-dark mb-2">إدارة الألعاب</h1>
          <p className="text-accent-dark/70">عرض وإدارة الألعاب النشطة والتاريخ</p>
        </div>
      </div>

      {/* Tabs */}
      <div className="flex gap-2 border-b-2 border-accent-greenLight/20">
        <button
          onClick={() => setActiveTab('active')}
          className={`px-6 py-3 font-medium transition-colors ${
            activeTab === 'active'
              ? 'text-secondary border-b-2 border-secondary'
              : 'text-accent-dark/60 hover:text-accent-dark'
          }`}
        >
          <Activity className="w-4 h-4 inline mr-2" />
          ألعاب نشطة
        </button>
        <button
          onClick={() => setActiveTab('history')}
          className={`px-6 py-3 font-medium transition-colors ${
            activeTab === 'history'
              ? 'text-secondary border-b-2 border-secondary'
              : 'text-accent-dark/60 hover:text-accent-dark'
          }`}
        >
          <Gamepad2 className="w-4 h-4 inline mr-2" />
          تاريخ الألعاب
        </button>
      </div>

      {/* Filters */}
      <Card className="border-2 border-accent-greenLight/20">
        <CardContent className="p-4">
          <div className="flex flex-col md:flex-row gap-4">
            <div className="flex-1 relative">
              <Search className="absolute right-3 top-1/2 transform -translate-y-1/2 text-accent-dark/50 w-5 h-5" />
              <Input
                placeholder="بحث في الألعاب..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pr-10"
              />
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Games List */}
      {filteredGames.length === 0 ? (
        <Card className="border-2 border-accent-greenLight/20">
          <CardContent className="p-12 text-center">
            <Gamepad2 className="w-16 h-16 mx-auto mb-4 text-accent-dark/30" />
            <p className="text-lg text-accent-dark/70 mb-2">
              {activeTab === 'active' ? 'لا توجد ألعاب نشطة' : 'لا يوجد تاريخ ألعاب'}
            </p>
            <p className="text-sm text-accent-dark/50">
              {searchTerm ? 'لم يتم العثور على ألعاب تطابق البحث' : 'سيتم عرض الألعاب هنا'}
            </p>
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-4">
          {filteredGames.map((game) => (
            <Card key={game.id} className="border-2 border-accent-greenLight/20">
              <CardContent className="p-6">
                <div className="flex items-center justify-between">
                  <div className="flex-1">
                    <div className="flex items-center gap-3 mb-2">
                      <h3 className="text-lg font-bold text-accent-dark">{game.name}</h3>
                      <span className="px-2 py-1 text-xs bg-secondary/10 text-secondary rounded">
                        {game.roomCode}
                      </span>
                      <span className="px-2 py-1 text-xs bg-accent-greenLight/10 text-accent-greenDark rounded">
                        {game.status}
                      </span>
                    </div>
                    <div className="flex items-center gap-4 text-sm text-accent-dark/70">
                      <span>اللاعبين: {game.playerCount}</span>
                      <span>•</span>
                      <span>تاريخ الإنشاء: {new Date(game.createdAt).toLocaleDateString('ar-SA')}</span>
                    </div>
                  </div>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={async () => {
                      try {
                        const details = await gameService.getGameDetails(game.id);
                        toast.info(`تفاصيل اللعبة: ${details.name} - ${details.status}`);
                      } catch (error: any) {
                        toast.error('فشل تحميل تفاصيل اللعبة');
                      }
                    }}
                  >
                    التفاصيل
                  </Button>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
};
